<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import type { EstimatorReportSearchResultDto, MetricDto } from "@/dto";
import { RouteNames } from "@/router";
import { smartToFixed } from "@/services/utils";
import ProjectDetailViewExperimentsSearchResultMetricTooltip from "@/views/projects/ProjectDetailViewExperimentsSearchResultMetricTooltip.vue";

const { report } = defineProps<{ report: EstimatorReportSearchResultDto }>();
const route = useRoute();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);

const metrics = computed(() => {
  const displayedMetrics = report.metrics
    .filter((m) => m.position !== null)
    .sort((a, b) => b.data_source?.localeCompare(a.data_source));
  const results = new Map<string, { values: number[]; sources: string[]; dto: MetricDto }>();
  for (const m of displayedMetrics) {
    const name =
      m.verbose_name.length > 0 ? m.verbose_name : m.name.replace(`_${m.data_source}`, "");
    const existing = results.get(name) ?? { values: [], sources: [], dto: m };
    existing.values = [...existing.values, m.value];
    if (m.data_source !== null) {
      existing.sources = [...existing.sources, m.data_source];
    }
    results.set(name, existing);
  }
  return results;
});
</script>

<template>
  <div class="experiment-search-result-card">
    <div class="header label-s">
      <i class="icon icon-test-tube" />
      <span> {{ report.key }} ({{ report.estimator_class_name }}) </span>
    </div>
    <div class="metrics label-s">
      <div v-for="[name, metric] in metrics" :key="name" class="metric">
        <div class="name">
          <ProjectDetailViewExperimentsSearchResultMetricTooltip :metric="metric.dto" />
        </div>
        <div class="value">
          <span v-for="(value, index) in metric.values" :key="index">
            <FloatingTooltip
              v-if="metric.sources[index]?.length > 0"
              :text="metric.sources[index]"
              strategy="absolute"
            >
              <span class="help">
                {{ smartToFixed(value) }}
                {{ name.toLowerCase().indexOf("time") !== -1 ? "s" : "" }}
              </span>
            </FloatingTooltip>
            <template v-else>
              {{ smartToFixed(value) }}
              {{ name.toLowerCase().indexOf("time") !== -1 ? "s" : "" }}
            </template>
            <span v-if="metric.values.length > 1 && index < metric.values.length - 1"> / </span>
          </span>
        </div>
      </div>
    </div>
    <div class="details label-s">
      <RouterLink
        :to="{
          name: RouteNames.EstimatorReportDetail,
          params: { tenantName, projectName, reportId: report.id },
        }"
      >
        View details <i class="icon icon-arrow-right" />
      </RouterLink>
    </div>
  </div>
</template>

<style scoped>
.experiment-search-result-card {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-6);
  border-radius: var(--radius-s);
  background-color: var(--color-background-secondary);
  gap: var(--spacing-4);

  .header {
    display: flex;
    height: 50px;
    align-items: center;
    color: var(--color-text-primary);
    gap: var(--spacing-8);

    .icon {
      color: var(--color-text-secondary);
      font-size: 1.6rem;
    }
  }

  .metrics,
  .details {
    border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    border-radius: var(--radius-s);
    background-color: var(--color-background-primary);
  }

  .metrics {
    .metric {
      display: flex;
      justify-content: space-between;
      margin: var(--spacing-8);

      .name {
        color: var(--color-text-secondary);

        & > span {
          text-decoration: underline dashed;
          text-underline-offset: var(--spacing-4);
        }

        .sources {
          text-decoration: none;
        }
      }

      .value {
        color: var(--color-text-primary);

        .help {
          cursor: help;
        }
      }
    }
  }

  .details {
    a {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-6) 0;
      color: var(--color-text-primary);
      gap: var(--spacing-6);
      text-decoration: none;

      i {
        transition: transform var(--animation-duration) var(--animation-easing);
      }

      &:hover {
        i {
          transform: translateX(2px);
        }
      }
    }
  }
}
</style>
